import { ReactElement, useState } from 'react';
import { useLocation } from 'wouter';
import { CornerUpRight, Mail, Scissors } from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import { useToast } from '@/components/ux/Toast';
import { useUnmergeTriageThread } from '../../api/useAdminTriage';
import { timeAgo } from '../ops/opsUi';

// All messages across the ticket's threads in one chronological timeline.
// Inbound sits left in a neutral bubble, outbound sits right in a primary
// tint. A subtle divider marks every thread change (subject + attach method),
// with a "Detach to new ticket" action when the ticket has more than one
// thread.

const COLLAPSE_AT = 700;

function MessageBubble({ message }: { message: AdminAPI.TriageMessageInfo }) {
  const [expanded, setExpanded] = useState(false);
  const inbound = message.direction === 'inbound';
  const body = message.bodyText ?? message.snippet ?? '';
  const isLong = body.length > COLLAPSE_AT;
  const shown = isLong && !expanded ? `${body.slice(0, COLLAPSE_AT).trimEnd()}…` : body;

  return (
    <div className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`max-w-[85%] rounded-xl px-3 py-2 border ${
          inbound
            ? 'bg-neutral-50 dark:bg-neutral-800/60 border-neutral-200 dark:border-neutral-700'
            : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
        }`}
      >
        <div className="flex items-center gap-2 text-[11px] text-dark-muted mb-1">
          {!inbound && <CornerUpRight className="w-3 h-3" />}
          <span className="font-medium truncate">
            {message.fromName ?? message.fromEmail ?? (inbound ? 'Sender' : 'Outclose')}
          </span>
          <span className="whitespace-nowrap">{timeAgo(message.sentAt)}</span>
        </div>
        <p className="text-sm whitespace-pre-wrap break-words">{shown}</p>
        {isLong && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-1 text-xs text-primary-500 font-medium hover:underline"
          >
            {expanded ? 'Show less' : 'Show full message'}
          </button>
        )}
      </div>
    </div>
  );
}

function ThreadDivider({
  thread,
  canDetach,
}: {
  thread: AdminAPI.TriageThreadInfo;
  canDetach: boolean;
}) {
  const toast = useToast();
  const [, navigate] = useLocation();
  const unmerge = useUnmergeTriageThread();

  return (
    <div className="flex items-center gap-2 text-[11px] text-dark-muted my-2">
      <span className="flex-1 border-t border-dashed border-neutral-200 dark:border-neutral-700" />
      <Mail className="w-3 h-3 shrink-0" />
      <span className="truncate max-w-[50%]" title={thread.subject ?? undefined}>
        {thread.subject ?? '(no subject)'}
      </span>
      {canDetach && (
        <button
          onClick={() =>
            unmerge.mutate(thread.id, {
              onSuccess: (res) => {
                toast.success('Thread detached to a new ticket');
                if (res.data?.newTicketId) navigate(`/triage/${res.data.newTicketId}`);
              },
              onError: (err) => toast.error(err instanceof Error ? err.message : 'Detach failed'),
            })
          }
          disabled={unmerge.isPending}
          title="Detach this thread onto a fresh ticket"
          aria-label="Detach to new ticket"
          className="hover:text-primary-500 disabled:opacity-50"
        >
          <Scissors className="w-3 h-3" />
        </button>
      )}
      <span className="flex-1 border-t border-dashed border-neutral-200 dark:border-neutral-700" />
    </div>
  );
}

export default function TriageConversation({
  threads,
  messages,
}: {
  threads: AdminAPI.TriageThreadInfo[];
  messages: AdminAPI.TriageMessageInfo[];
}) {
  const threadById = new Map(threads.map((t) => [t.id, t]));
  const sorted = [...messages].sort(
    (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
  );
  const canDetach = threads.length > 1;

  if (sorted.length === 0) {
    return <p className="text-sm text-dark-muted">No messages yet.</p>;
  }

  const items: ReactElement[] = [];
  let lastThreadId: string | null = null;
  for (const message of sorted) {
    if (message.threadId !== lastThreadId) {
      const thread = threadById.get(message.threadId);
      if (thread) {
        items.push(
          <ThreadDivider key={`divider-${message.id}`} thread={thread} canDetach={canDetach} />
        );
      }
      lastThreadId = message.threadId;
    }
    items.push(<MessageBubble key={message.id} message={message} />);
  }

  return <div className="space-y-2">{items}</div>;
}
